import React from "react";
import { useFormContext } from "react-hook-form";
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  Box,
  Paper,
  List,
  ListItem,
  ListItemText,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
} from "@mui/material";
import { useState, useEffect } from "react";
import { IconButton } from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import ContentCopyRounded from "@mui/icons-material/ContentCopyRounded";
import FilterAltRounded from "@mui/icons-material/FilterAltRounded";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FolderRounded from "@mui/icons-material/FolderRounded";
import newApi from "service/newApi";

function PipelineSummary({ step }) {
  const { watch } = useFormContext();
  const dataSource = watch("data_source");
  const dataSourceType = watch("data_source_type");
  const metadataFiles = watch("metadata_source_files") || [];
  const provider = watch("provider");
  const language = watch("language");
  const vocabularyLibrary = watch("vocabularyLibrary");
  const analysisType = watch("analysis_type");
  const client = watch("client");
  const segment = watch("segment");
  const prompts = watch("prompts") || [];
  const title = watch("title");
  const description = watch("description");
  const libraries = watch("libraries") || [];
  const librariesCount = Array.isArray(libraries) ? libraries.length : 0;
  const folders = watch("folders");
  const cachedFoldersMap = watch("CachedFolders");
  const cachedRootFolders = watch("CachedRootFolders") || [];
  const metadataIsFilter = watch("metadataIsFilter");
  const metadataSourceType = watch("metadata_source_type");
  const metadataSourceQuery = watch("metadataSourceQuery");

  const metadataFilters = watch("metadata_filters") || [];
  const metadataFilterGroups = watch("metadata_filter_pools") || [];
  const filteredFiles = watch("filtered_files") || [];
  const metadataRecordsLimit = watch("metadata_records_limit");
  const eventDistributionColumn = watch("event_distribution_column");
  const eventDistributionLimit = watch("event_distribution_limit");

  const [dataProcessingConfigured, setDataProcessingConfigured] =
    useState(null);
  const [loadingDataProcessingConfig, setLoadingDataProcessingConfig] =
    useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [showAllLegacyFilters, setShowAllLegacyFilters] = useState(false);
  const [showFolderTree, setShowFolderTree] = useState(false);

  const MAX_VISIBLE_FILTERS = 5;

  const providerName = provider === "link2go" ? "Link 2 Go" : provider || "-";
  const languageLabel = language?.name || "-";
  const analysisTypeLabel =
    analysisType === "generative"
      ? "Análise generativa"
      : analysisType === "library-matching"
        ? "Enquadramento de biblioteca"
        : "-";
  const dataSourceLabel =
    dataSource === "LOCAL"
      ? "Meu Computador"
      : dataSource === "CLOUD"
        ? "Nuvem Concentrix"
        : "-";
  const dataTypeLabel = dataSourceType || "-";
  const segmentLabel = segment?.name || "-";
  const clientLabel = client?.name || "-";
  const promptsCount = Array.isArray(prompts) ? prompts.length : 0;

  const operatorOptions = [
    { value: "equals", label: "Igual a" },
    { value: "not_equals", label: "Diferente de" },
    { value: "greater", label: "Maior que" },
    { value: "greater_or_equal", label: "Maior ou Igual a" },
    { value: "less", label: "Menor que" },
    { value: "less_or_equal", label: "Menor ou Igual" },
    { value: "contains", label: "Contém" },
    { value: "not_contains", label: "Não Contém" },
  ];

  const getOperatorLabel = (operatorValue) => {
    const operator = operatorOptions.find((op) => op.value === operatorValue);
    return operator ? operator.label : operatorValue;
  };

  const checkDataProcessingConfig = async (clientId) => {
    if (!clientId) return;

    setLoadingDataProcessingConfig(true);
    try {
      const response = await newApi.get(
        `/v1/data-processing-config/${clientId}`,
      );

      const hasDataFields =
        response.data &&
        response.data.dataFields &&
        response.data.dataFields.length > 0;
      setDataProcessingConfigured(hasDataFields);
    } catch (error) {
      if (error.response?.status === 404) {
        setDataProcessingConfigured(false);
      } else {
        console.error(
          "Erro ao buscar configuração de tratamento de dados:",
          error,
        );
        setDataProcessingConfigured(null);
      }
    } finally {
      setLoadingDataProcessingConfig(false);
    }
  };

  useEffect(() => {
    if (client && client.id) {
      checkDataProcessingConfig(client.id);
    } else {
      setDataProcessingConfigured(null);
    }
  }, [client]);

  const getDataProcessingStatus = () => {
    if (loadingDataProcessingConfig) {
      return "Verificando...";
    }
    if (dataProcessingConfigured === null) {
      return "Não verificado";
    }
    return dataProcessingConfigured ? "Configurado" : "Não configurado";
  };

  const buildFolderTree = () => {
    if (!folders?.length) return [];
    const cached =
      cachedFoldersMap instanceof Map ? cachedFoldersMap : new Map();
    const selectedSet = new Set(folders);

    if (cached.size === 0) {
      return folders.map((id) => ({
        id,
        label: id,
        isSelected: true,
        children: [],
      }));
    }

    const childToParent = new Map();
    for (const [parentId, children] of cached.entries()) {
      for (const child of children) {
        if (child.id) childToParent.set(child.id, parentId);
      }
    }

    const relevantIds = new Set(folders);
    for (const folderId of folders) {
      let current = folderId;
      while (childToParent.has(current)) {
        const parentId = childToParent.get(current);
        relevantIds.add(parentId);
        current = parentId;
      }
    }

    const rootLabelMap = new Map();
    for (const rf of cachedRootFolders) {
      if (rf.id) rootLabelMap.set(rf.id, rf.label || rf.name || rf.id);
    }

    const buildChildren = (parentId) =>
      (cached.get(parentId) || [])
        .filter(
          (child) => relevantIds.has(child.id) || selectedSet.has(child.id),
        )
        .map((child) => ({
          id: child.id,
          label: child.label || child.name || child.id,
          isSelected: selectedSet.has(child.id),
          children: buildChildren(child.id),
        }));

    const rootKeys = [...cached.keys()].filter(
      (key) => !childToParent.has(key) && relevantIds.has(key),
    );

    return rootKeys.map((rootId) => ({
      id: rootId,
      label: rootLabelMap.get(rootId) || rootId,
      isSelected: selectedSet.has(rootId),
      children: buildChildren(rootId),
    }));
  };

  const collectFolderText = (nodes, depth = 0) =>
    nodes
      .flatMap((node) => [
        `${"  ".repeat(depth)}${node.isSelected ? "* " : ""}${node.label}`,
        ...collectFolderText(node.children || [], depth + 1),
      ])
      .join("\n");

  const handleCopyFolders = () => {
    const text = collectFolderText(buildFolderTree());
    navigator.clipboard.writeText(text);
  };

  const renderFolderNode = (node, depth = 0) => (
    <Box key={node.id}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          pl: depth * 2,
          py: 0.25,
          px: 1,
          backgroundColor: node.isSelected
            ? "rgba(94, 114, 228, 0.08)"
            : "transparent",
        }}
      >
        <FolderRounded
          sx={{
            fontSize: 14,
            color: node.isSelected ? "#5e72e4" : "#90a4ae",
          }}
        />
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: node.isSelected ? 600 : 400,
            color: node.isSelected ? "#5e72e4" : "text.secondary",
          }}
        >
          {node.label}
        </Typography>
      </Box>
      {node.children?.map((child) => renderFolderNode(child, depth + 1))}
    </Box>
  );

  const renderDataSource = () => (
    <Box mb={2}>
      <Typography variant="subtitle1" fontWeight="bold">
        Fonte de Dados
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Cliente: <b>{clientLabel}</b>
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Tratamento de dados: <b>{getDataProcessingStatus()}</b>
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Fonte: <b>{dataSourceLabel}</b>
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Tipo de dados: <b>{dataTypeLabel}</b>
      </Typography>
      <Box sx={{ mb: 0.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {folders ? folders.length : 0} pastas selecionadas
          </Typography>
          {folders && folders.length > 0 && (
            <Button
              size="small"
              onClick={() => setShowFolderTree((prev) => !prev)}
              sx={{
                fontSize: 11,
                py: 0,
                minWidth: "auto",
                textTransform: "none",
              }}
              startIcon={
                <ExpandMoreIcon
                  sx={{
                    fontSize: 14,
                    transform: showFolderTree ? "rotate(180deg)" : "none",
                    transition: "transform 0.2s",
                  }}
                />
              }
            >
              {showFolderTree ? "Ocultar" : "Ver pastas"}
            </Button>
          )}
        </Box>
        {showFolderTree && folders && folders.length > 0 && (
          <Paper
            variant="outlined"
            sx={{
              mt: 0.5,
              maxHeight: 150,
              overflowY: "auto",
              background: "#fafbfc",
              py: 0.5,
              px: 0,
              position: "relative",
            }}
          >
            <IconButton
              size="small"
              onClick={handleCopyFolders}
              title="Copiar todas as pastas"
              sx={{
                position: "absolute",
                top: 2,
                right: 2,
                color: "text.secondary",
                "&:hover": { color: "#5e72e4" },
              }}
            >
              <ContentCopyRounded sx={{ fontSize: 14 }} />
            </IconButton>
            {buildFolderTree().map((node) => renderFolderNode(node))}
          </Paper>
        )}
      </Box>
    </Box>
  );

  const renderMetadata = () => (
    <Box mb={2}>
      <Typography variant="subtitle1" fontWeight="bold">
        Metadados
      </Typography>

      {/* ▸ LOCAL + CSV upload  ---------------------------------------- */}
      {dataSource === "LOCAL" && (
        <Paper
          variant="outlined"
          sx={{
            maxHeight: 80,
            overflowY: "auto",
            background: "#fafbfc",
            p: 0,
            mb: 1,
          }}
        >
          {metadataFiles?.length ? (
            <List dense disablePadding>
              {metadataFiles.map((file, idx) => (
                <ListItem key={idx} sx={{ py: 0.5, px: 1 }}>
                  <ListItemText
                    primary={
                      typeof file === "string" ? file : file?.name || "Arquivo"
                    }
                    primaryTypographyProps={{ fontSize: 13 }}
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography
              variant="body2"
              color="text.disabled"
              sx={{ px: 1, py: 0.5, fontSize: 13 }}
            >
              Nenhum arquivo selecionado
            </Typography>
          )}
        </Paper>
      )}

      {/* ▸ CLOUD + CSV metadata --------------------------------------- */}
      {dataSource === "CLOUD" && metadataSourceType === "csv" && (
        <Paper
          variant="outlined"
          sx={{
            background: "#fafbfc",
            p: 1,
            display: "flex",
            alignItems: "center",
          }}
        >
          <DescriptionIcon sx={{ mr: 1, color: "text.secondary" }} />
          <Typography variant="body2" sx={{ fontSize: 13 }}>
            Arquivo CSV sera consultado dentro de <b>cada</b> pasta selecionada
            como&nbsp;
            <em>Fonte de Dados</em>.
          </Typography>
        </Paper>
      )}

      {/* ▸ CLOUD  -------------------- */}
      {dataSource === "CLOUD" && (
        <Paper
          variant="outlined"
          sx={{
            maxHeight: 80,
            overflowY: "auto",
            background: "#fafbfc",
            p: 0,
            mb: 1,
          }}
        >
          {metadataFiles?.length ? (
            <List dense disablePadding>
              {metadataFiles.map((file, idx) => (
                <ListItem key={idx} sx={{ py: 0.5, px: 1 }}>
                  <ListItemText
                    primary={
                      typeof file === "string" ? file : file?.name || "Arquivo"
                    }
                    primaryTypographyProps={{ fontSize: 13 }}
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography
              variant="body2"
              color="text.disabled"
              sx={{ px: 1, py: 0.5, fontSize: 13 }}
            >
              Nenhum arquivo selecionado
            </Typography>
          )}
        </Paper>
      )}

      {/* ▸ CLOUD + QUERY metadata  ------------------------------------ */}
      {dataSource === "CLOUD" && metadataSourceType === "query" && (
        <Paper
          variant="outlined"
          sx={{
            background: "#272822",
            color: "white",
            position: "relative",
            p: 1.5,
            fontFamily: "monospace",
            fontSize: 13,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            maxHeight: 150,
            overflowY: "scroll",
          }}
        >
          {metadataSourceQuery || "-- sem query --"}
          <IconButton
            size="small"
            sx={{ position: "absolute", top: 4, right: 4, color: "white" }}
            aria-label="Copiar query"
            onClick={() =>
              navigator.clipboard.writeText(metadataSourceQuery || "")
            }
          >
            <ContentCopyRounded fontSize="inherit" />
          </IconButton>
        </Paper>
      )}
    </Box>
  );

  const renderFilters = () => {
    // Filter only valid groups with valid conditions
    const validGroups = metadataFilterGroups
      .map((group) => {
        if (!group.conditions || group.conditions.length === 0) return null;

        const validConditions = group.conditions.filter(
          (condition) =>
            condition.column &&
            condition.column.trim() !== "" &&
            condition.operator &&
            condition.operator.trim() !== "" &&
            condition.value &&
            condition.value.trim() !== "",
        );

        if (validConditions.length === 0) return null;

        return { ...group, conditions: validConditions };
      })
      .filter((group) => group !== null);

    const hasMetadataFilterGroups = validGroups.length > 0;

    // Filter only valid legacy filters
    const validLegacyFilters = metadataFilters.filter(
      (filter) =>
        filter.column &&
        filter.column.trim() !== "" &&
        filter.operator &&
        filter.operator.trim() !== "" &&
        filter.value &&
        filter.value.trim() !== "",
    );

    const hasMetadataFilters = validLegacyFilters.length > 0;
    const hasFilteredFiles = filteredFiles.length > 0;

    // Helper to render a single condition as a chip-style element
    const renderConditionChip = (condition, showAnd = false) => (
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          mr: 0.5,
          mb: 0.5,
        }}
      >
        {showAnd && (
          <Chip
            label="E"
            size="small"
            sx={{
              height: 20,
              fontSize: 10,
              fontWeight: "bold",
              backgroundColor: "#e8f5e9",
              color: "#2e7d32",
            }}
          />
        )}
        <Chip
          size="small"
          sx={{
            height: "auto",
            py: 0.25,
            "& .MuiChip-label": {
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              whiteSpace: "normal",
              fontSize: 11,
            },
          }}
          label={
            <>
              <Box
                component="span"
                sx={{ fontWeight: "bold", color: "#1565c0" }}
              >
                {condition.column}
              </Box>
              <Box component="span" sx={{ color: "#666", fontStyle: "italic" }}>
                {getOperatorLabel(condition.operator)}
              </Box>
              <Box
                component="span"
                sx={{ fontWeight: "bold", color: "#c62828" }}
              >
                &quot;{condition.value}&quot;
              </Box>
            </>
          }
        />
      </Box>
    );

    // Calculate total conditions count
    const totalConditions = validGroups.reduce(
      (acc, group) => acc + group.conditions.length,
      0,
    );

    return (
      <Box mb={2}>
        <Typography variant="subtitle1" fontWeight="bold">
          Filtros
        </Typography>

        {metadataIsFilter ? (
          <>
            <Paper
              variant="outlined"
              sx={{
                background: "#fafbfc",
                p: 1,
                display: "flex",
                alignItems: "center",
                mb: 1,
              }}
            >
              <FilterAltRounded sx={{ mr: 1, color: "text.secondary" }} />
              <Typography variant="body2" sx={{ fontSize: 13 }}>
                Os metadados configurados acima serão usados como <b>filtro</b>;
                somente eventos que possuam esses metadados serão processados.
              </Typography>
            </Paper>

            {hasMetadataFilterGroups && (
              <Box sx={{ mb: 1 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontSize: 12, color: "text.secondary", mb: 0.5 }}
                >
                  {validGroups.length}{" "}
                  {validGroups.length === 1 ? "grupo" : "grupos"} •{" "}
                  {totalConditions}{" "}
                  {totalConditions === 1 ? "condição" : "condições"}
                </Typography>

                {validGroups.map((group, groupIdx) => {
                  const groupKey = group.id || groupIdx;
                  const isExpanded = expandedGroups[groupKey] ?? groupIdx === 0;
                  const conditionsToShow = isExpanded
                    ? group.conditions
                    : group.conditions.slice(0, 2);
                  const hasMoreConditions = group.conditions.length > 2;

                  return (
                    <Accordion
                      key={groupKey}
                      expanded={isExpanded}
                      onChange={() =>
                        setExpandedGroups((prev) => ({
                          ...prev,
                          [groupKey]: !isExpanded,
                        }))
                      }
                      sx={{
                        boxShadow: "none",
                        border: "1px solid #e0e0e0",
                        "&:before": { display: "none" },
                        mb: 0.5,
                        "&.Mui-expanded": { mb: 0.5 },
                      }}
                      disableGutters
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
                        sx={{
                          minHeight: 36,
                          px: 1,
                          backgroundColor: "#f5f5f5",
                          "&.Mui-expanded": { minHeight: 36 },
                          "& .MuiAccordionSummary-content": { my: 0.5 },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            width: "100%",
                          }}
                        >
                          <Chip
                            label={`Grupo ${groupIdx + 1}`}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: 11,
                              fontWeight: "bold",
                              backgroundColor: "#1976d2",
                              color: "white",
                            }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {group.conditions.length}{" "}
                            {group.conditions.length === 1
                              ? "condição"
                              : "condições"}
                            {group.limit && ` • Limite: ${group.limit}`}
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 1, pt: 0.5 }}>
                        <Box sx={{ display: "flex", flexWrap: "wrap" }}>
                          {conditionsToShow.map((condition, condIdx) =>
                            renderConditionChip(condition, condIdx > 0),
                          )}
                          {!isExpanded && hasMoreConditions && (
                            <Chip
                              label={`+${group.conditions.length - 2} mais`}
                              size="small"
                              variant="outlined"
                              sx={{
                                height: 20,
                                fontSize: 10,
                                color: "#666",
                                cursor: "pointer",
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedGroups((prev) => ({
                                  ...prev,
                                  [groupKey]: true,
                                }));
                              }}
                            />
                          )}
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Box>
            )}

            {hasMetadataFilters && !hasMetadataFilterGroups && (
              <Box sx={{ mb: 1 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontSize: 12, color: "text.secondary", mb: 0.5 }}
                >
                  {validLegacyFilters.length} filtros (legado)
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    background: "#fafbfc",
                    p: 1,
                  }}
                >
                  <Box sx={{ display: "flex", flexWrap: "wrap" }}>
                    {(showAllLegacyFilters
                      ? validLegacyFilters
                      : validLegacyFilters.slice(0, MAX_VISIBLE_FILTERS)
                    ).map((filter, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.5,
                          mr: 0.5,
                          mb: 0.5,
                        }}
                      >
                        <Chip
                          size="small"
                          sx={{
                            height: "auto",
                            py: 0.25,
                            "& .MuiChip-label": {
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              whiteSpace: "normal",
                              fontSize: 11,
                            },
                          }}
                          label={
                            <>
                              <Box
                                component="span"
                                sx={{ fontWeight: "bold", color: "#1565c0" }}
                              >
                                {filter.column}
                              </Box>
                              <Box
                                component="span"
                                sx={{ color: "#666", fontStyle: "italic" }}
                              >
                                {getOperatorLabel(filter.operator)}
                              </Box>
                              <Box
                                component="span"
                                sx={{ fontWeight: "bold", color: "#c62828" }}
                              >
                                &quot;{filter.value}&quot;
                              </Box>
                              {filter.limit && (
                                <Box
                                  component="span"
                                  sx={{
                                    fontSize: 10,
                                    color: "#666",
                                    fontStyle: "italic",
                                  }}
                                >
                                  (máx: {filter.limit})
                                </Box>
                              )}
                            </>
                          }
                        />
                      </Box>
                    ))}
                    {validLegacyFilters.length > MAX_VISIBLE_FILTERS && (
                      <Button
                        size="small"
                        onClick={() =>
                          setShowAllLegacyFilters(!showAllLegacyFilters)
                        }
                        sx={{ fontSize: 11, py: 0, minWidth: "auto" }}
                      >
                        {showAllLegacyFilters
                          ? "Ver menos"
                          : `+${validLegacyFilters.length - MAX_VISIBLE_FILTERS} mais`}
                      </Button>
                    )}
                  </Box>
                </Paper>
              </Box>
            )}

            {hasFilteredFiles && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: 13 }}
              >
                Arquivos filtrados: <b>{filteredFiles.length}</b>
              </Typography>
            )}

            {metadataRecordsLimit && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: 13, mt: 0.5 }}
              >
                Limite global de processamento:{" "}
                <b>{metadataRecordsLimit} registros</b>
              </Typography>
            )}

            {eventDistributionColumn && eventDistributionLimit > 0 && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: 13, mt: 0.5 }}
              >
                Distribuição de eventos:{" "}
                <b>
                  {eventDistributionLimit}{" "}
                  {eventDistributionLimit === 1 ? "evento" : "eventos"} por
                  valor de &quot;{eventDistributionColumn}&quot;
                </b>
              </Typography>
            )}
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {dataSource === "LOCAL" ? "Filtros não configurados" : "-"}
          </Typography>
        )}
      </Box>
    );
  };

  const renderTranscription = () => (
    <Box mb={2}>
      <Typography variant="subtitle1" fontWeight="bold">
        Configuração de Transcrição
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Provedor: <b>{providerName}</b>
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Idioma: <b>{languageLabel}</b>
      </Typography>
      {vocabularyLibrary && (
        <Typography variant="body2" color="text.secondary">
          Biblioteca de Vocabulário: <b>{vocabularyLibrary.title}</b>
        </Typography>
      )}
    </Box>
  );

  const renderAnalysisType = () => (
    <Box mb={2}>
      <Typography variant="subtitle1" fontWeight="bold">
        Tipo de Análise
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {analysisTypeLabel}
      </Typography>
    </Box>
  );

  const renderAnalysisConfig = () => (
    <Box mb={2}>
      <Typography variant="subtitle1" fontWeight="bold">
        Configuração da Análise
      </Typography>

      <Typography variant="body2" color="text.secondary">
        Segmento: <b>{segmentLabel}</b>
      </Typography>

      <Typography variant="body2" color="text.secondary">
        {analysisType === "library-matching"
          ? "Bibliotecas selecionadas:"
          : "Prompts selecionados:"}{" "}
        <b>
          {analysisType === "library-matching" ? librariesCount : promptsCount}
        </b>
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Título: <b>{title || <span style={{ color: "#888" }}>-</span>}</b>
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Descrição:{" "}
        <b>{description || <span style={{ color: "#888" }}>-</span>}</b>
      </Typography>
    </Box>
  );

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardHeader
        title={
          <Typography variant="h6" fontWeight="bold">
            Sumário da Configuração
          </Typography>
        }
        sx={{ pb: 0.5, pt: 1.5, background: "#f5f5f5" }}
      />
      <CardContent sx={{ pt: 1.5, pb: 1.5 }}>
        {step >= 1 && renderDataSource()}
        {step >= 2 && renderMetadata()}
        {step >= 3 && renderFilters()}
        {step >= 4 && renderTranscription()}
        {step >= 5 && renderAnalysisType()}
        {step >= 6 && renderAnalysisConfig()}
      </CardContent>
    </Card>
  );
}

export default PipelineSummary;
